// Drives umami-kit (services/vendor/umami-kit.js, appended to the tracker at
// GET /w/m.js) - the piece that answers "how long was this visitor actually
// active on this page, and how long were they idle".
//
// umami-kit was written for multi-page sites, and this app is a client-side
// router. Three of its behaviours have to be worked around rather than
// configured away; the vendored copy is kept byte-identical to upstream so it
// can be diffed on update, which means every workaround lives here.
// services/vendor/README.md lists them all.

// CONSTRUCTED EXACTLY ONCE PER PAGE LOAD, NEVER RE-CONSTRUCTED. The kit's
// destroy() clears only its heartbeat timer and leaves every scroll/idle/
// click/visibility listener and its 30s idle setInterval attached - so
// tearing one down and building another per route change would pile up
// duplicate listeners and duplicate events for the life of the tab.
let kit = null;

// 60s rather than the kit's own 30s default. The heartbeat is per active
// visitor for as long as they stay on a page, so it is by far the highest-
// volume event this integration produces - and umami Cloud bills per event.
// 30s costs twice as much for resolution nobody is reading.
const HEARTBEAT_INTERVAL_MS = 60_000;

export function startKit() {
  if (kit || typeof window === 'undefined') return;
  const UmamiTracker = window.UmamiTracker;
  if (typeof UmamiTracker !== 'function') return; // analytics off, or blocked

  kit = new UmamiTracker({
    heartbeatInterval: HEARTBEAT_INTERVAL_MS,
    // Explicit events at real call sites, not a firehose of every click on
    // the page - see app/_shared/analytics/events.js for the catalogue.
    autoTrackAllClicks: false
  });
}

// Called on every client-side route change.
//
// The kit sets state.startTime and state.scrollDepthTracked once, in its
// constructor, and never resets them - so without this, every time-on-page
// heartbeat and every page-exit on a routed app reports time since the app
// BOOTED rather than time on the page being left, and scroll depth is only
// ever reported once per tab.
//
// Reaching into state is deliberate. The kit exposes no reset of any kind, and
// the alternative (re-constructing it) leaks listeners - see above. If a newer
// upstream grows a real API for this, replace the three writes below with it.
export function resetForNavigation() {
  if (!kit) return;
  kit.state.startTime = Date.now();
  kit.state.lastActivity = Date.now();
  kit.state.scrollDepthTracked = [];
  kit.state.visibleElements.clear();
  kit.state.isIdle = false;
}
